import React, { useEffect, useMemo, useState } from "react";
import { Globe, Building2, Users } from "lucide-react";
import { api } from "./api";
import { useAuth } from "./AuthContext";

/**
 * Phase 2b2 — Two-level scope selector visible only to mssp_* and
 * platform_* users. tenant_* users have nothing to choose: their
 * effective scope is fixed by the JWT and any override is rejected
 * 403 by the backend, so we render `null` for them.
 *
 * Layout:
 *   [Globe ▼]  active when claim_scope = Platform
 *      "Platform"
 *   [Building2 ▼]  list of MSSPs (own MSSP only for mssp_*; all for platform_*)
 *      "All MSSPs" / "Acme Security"
 *   [Users ▼]  list of tenants under the selected MSSP
 *      "All tenants" / "Bank EU 1"
 *
 * The component fetches `/api/platform/mssps` once; tenants are fetched
 * lazily when the user picks an MSSP. Errors are swallowed — the
 * dropdown just stays empty if the backend isn't reachable, since
 * blocking the navigation header on a network blip is worse UX than
 * a degraded selector.
 */
export default function ScopeSelector() {
  const { user, scopeOverride, setScopeOverride } = useAuth();
  const [mssps, setMssps] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loadingTenants, setLoadingTenants] = useState(false);

  const role = user?.role_v2 ?? null;
  const isMssp = role === "mssp_admin" || role === "mssp_analyst";
  const isPlatform = role === "platform_admin" || role === "platform_ops";
  const visible = isMssp || isPlatform;

  // For mssp_*, the MSSP is fixed by the claim — we still fetch it so
  // the dropdown can show the display name. For platform_*, the user
  // can pick any MSSP.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/platform/mssps");
        if (!cancelled) setMssps(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setMssps([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // The currently-active MSSP id for the tenant dropdown is either the
  // override (platform_* who picked one) or the claim's own MSSP.
  const activeMsspId = useMemo(() => {
    if (scopeOverride.mssp_id) return scopeOverride.mssp_id;
    if (isMssp) return user?.mssp_id ?? null;
    return null;
  }, [scopeOverride.mssp_id, isMssp, user?.mssp_id]);

  useEffect(() => {
    if (!visible || !activeMsspId) {
      setTenants([]);
      return;
    }
    let cancelled = false;
    setLoadingTenants(true);
    (async () => {
      try {
        const { data } = await api.get(`/api/mssps/${activeMsspId}/tenants`);
        if (!cancelled) setTenants(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setTenants([]);
      } finally {
        if (!cancelled) setLoadingTenants(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, activeMsspId]);

  if (!visible) return null;

  const onMsspChange = (e) => {
    const v = e.target.value || null;
    // Picking a different MSSP clears the tenant choice — otherwise we
    // could keep a stale tenant id from the previous MSSP, which the
    // backend would reject 403.
    setScopeOverride({ mssp_id: v, tenant_id: null });
  };

  const onTenantChange = (e) => {
    const v = e.target.value || null;
    setScopeOverride({ mssp_id: scopeOverride.mssp_id, tenant_id: v });
  };

  // platform_* see an "All MSSPs" entry to revert to Platform-wide.
  // mssp_* see only their MSSP and cannot leave it.
  const canPickAllMssps = isPlatform;

  return (
    <div
      className="flex items-center gap-2 text-xs"
      data-testid="scope-selector"
    >
      {isPlatform && (
        <Globe size={14} color="#94a3b8" aria-label="Platform scope" />
      )}
      {!isPlatform && <Building2 size={14} color="#94a3b8" />}
      <select
        aria-label="MSSP scope"
        value={scopeOverride.mssp_id ?? ""}
        onChange={onMsspChange}
        disabled={!canPickAllMssps && mssps.length <= 1}
        style={{ maxWidth: "12rem" }}
      >
        {canPickAllMssps && <option value="">All MSSPs</option>}
        {mssps.map((m) => (
          <option key={m.id} value={m.id}>
            {m.display_name}
          </option>
        ))}
      </select>

      <Users size={14} color="#94a3b8" />
      <select
        aria-label="Tenant scope"
        value={scopeOverride.tenant_id ?? ""}
        onChange={onTenantChange}
        disabled={!activeMsspId || loadingTenants}
        style={{ maxWidth: "12rem" }}
      >
        <option value="">{activeMsspId ? "All tenants" : "Pick MSSP"}</option>
        {tenants.map((t) => (
          <option key={t.id} value={t.id}>
            {t.display_name}
          </option>
        ))}
      </select>
    </div>
  );
}
